<template>
  <div class="container mt-5">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active">
          <router-link to="/dashboard/ai-support" aria-current="true">AI Support</router-link>
        </li>
      </ul>
    </nav>

    <div class="hero is-primary">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-vcentered">
            <div class="column">
              <h1 class="title is-2 has-text-white">
                <font-awesome-icon icon="headset" class="mr-3" />
                DespaQ AI Assistant
              </h1>
              <h2 class="subtitle is-4 has-text-white">
                Trained specifically for AmMetLife E-Invoicing Middleware
              </h2>
              <div class="buttons">
                <button class="button is-light is-large" @click="openChatbot">
                  <font-awesome-icon icon="comments" class="mr-2" />
                  Start Chatting
                </button>
                <button class="button is-primary is-inverted is-outlined" @click="scrollToFeatures">
                  <font-awesome-icon icon="info-circle" class="mr-2" />
                  Learn More
                </button>
              </div>
            </div>
            <div class="column">
              <figure class="image">
                <div class="chatbot-preview">
                  <font-awesome-icon icon="headset" class="chatbot-icon" />
                </div>
              </figure>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="section" ref="featuresSection">
      <div class="container">
        <h2 class="title has-text-centered mb-6">What can the AI Assistant help you with?</h2>
        
        <div class="columns is-multiline">
          <div class="column is-one-third" v-for="feature in features" :key="feature.id">
            <div class="card feature-card">
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <figure class="image is-48x48">
                      <div class="feature-icon">
                        <font-awesome-icon :icon="feature.icon" />
                      </div>
                    </figure>
                  </div>
                  <div class="media-content">
                    <p class="title is-5">{{ feature.title }}</p>
                  </div>
                </div>
                <div class="content">
                  {{ feature.description }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section has-background-light">
      <div class="container">
        <h2 class="title has-text-centered mb-6">Quick Start Examples</h2>
        
        <div class="columns">
          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  <font-awesome-icon icon="question-circle" class="mr-2" />
                  Common Questions
                </p>
              </div>
              <div class="card-content">
                <div class="content">
                  <div v-for="question in commonQuestions" :key="question" class="question-item">
                    <button 
                      class="button is-text is-fullwidth has-text-left"
                      @click="askQuestion(question)"
                    >
                      "{{ question }}"
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="column is-half">
            <div class="card">
              <div class="card-header">
                <p class="card-header-title">
                  <font-awesome-icon icon="lightbulb" class="mr-2" />
                  Pro Tips
                </p>
              </div>
              <div class="card-content">
                <div class="content">
                  <ul>
                    <li>Be specific with your questions for better answers</li>
                    <li>Ask about invoice statuses, error codes, and processes</li>
                    <li>Request step-by-step guides for complex procedures</li>
                    <li>Use the chat history to continue previous conversations</li>
                    <li>Ask for troubleshooting help when encountering issues</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="container">
        <h2 class="title has-text-centered mb-6">AI Assistant Status</h2>
        
        <div class="columns is-centered">
          <div class="column is-half">
            <div class="card">
              <div class="card-content">
                <div class="level">
                  <div class="level-left">
                    <div class="level-item">
                      <div>
                        <p class="title is-5">Service Status</p>
                        <p class="subtitle is-6">Real-time AI service monitoring</p>
                      </div>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <div class="field is-grouped">
                        <div class="control">
                          <span :class="['tag', 'is-large', healthStatusClass]">
                            <font-awesome-icon :icon="healthStatusIcon" class="mr-2" />
                            {{ healthStatusText }}
                          </span>
                        </div>
                        <div class="control">
                          <button 
                            class="button is-small" 
                            @click="checkHealth"
                            :class="{ 'is-loading': checking }"
                          >
                            <font-awesome-icon icon="sync-alt" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="healthData" class="content">
                  <hr>
                  <div class="columns is-multiline">
                    <div class="column is-half">
                      <strong>Total Knowledge Chunks:</strong> {{ healthData.stats?.total_chunks || 'N/A' }}
                    </div>
                    <div class="column is-half">
                      <strong>Embedding Model:</strong> {{ healthData.stats?.embedding_model || 'N/A' }}
                    </div>
                    <div class="column is-half">
                      <strong>Index Status:</strong> 
                      <span :class="healthData.stats?.index_exists ? 'has-text-success' : 'has-text-danger'">
                        {{ healthData.stats?.index_exists ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                    <div class="column is-half">
                      <strong>Test Query:</strong>
                      <span :class="healthData.test_query_success ? 'has-text-success' : 'has-text-danger'">
                        {{ healthData.test_query_success ? 'Passed' : 'Failed' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section has-background-primary">
      <div class="container has-text-centered">
        <h2 class="title has-text-white">Ready to get started?</h2>
        <p class="subtitle has-text-white">
          Click the chat button in the bottom-right corner or use the button below to start your conversation.
        </p>
        <button class="button is-large is-white" @click="openChatbot">
          <font-awesome-icon icon="comments" class="mr-2" />
          Open AI Assistant
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
  faHeadset, faComments, faInfoCircle, faQuestionCircle, faLightbulb,
  faSyncAlt, faCheckCircle, faTimesCircle, faSpinner, faFileInvoice,
  faCog, faLifeRing, faChartLine, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { chatbotService } from '@/api/chatbotApi';

library.add(
  faHeadset, faComments, faInfoCircle, faQuestionCircle, faLightbulb,
  faSyncAlt, faCheckCircle, faTimesCircle, faSpinner, faFileInvoice,
  faCog, faLifeRing, faChartLine, faShieldAlt
);

export default {
  name: 'AISupportPage',
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      healthData: null,
      checking: false,
      features: [
        {
          id: 1,
          icon: 'file-invoice',
          title: 'Invoice Management',
          description: 'Get help with creating, editing, and managing e-invoices. Learn about invoice statuses and approval workflows.'
        },
        {
          id: 2,
          icon: 'cog',
          title: 'System Configuration',
          description: 'Understand system settings, API configurations, and integration setups. Get guidance on troubleshooting.'
        },
        {
          id: 3,
          icon: 'life-ring',
          title: 'Technical Support',
          description: 'Resolve technical issues, understand error messages, and get step-by-step troubleshooting guides.'
        },
        {
          id: 4,
          icon: 'chart-line',
          title: 'Reporting & Analytics',
          description: 'Learn how to generate reports, understand dashboard metrics, and analyze your invoice data.'
        },
        {
          id: 5,
          icon: 'shield-alt',
          title: 'Security & Compliance',
          description: 'Get information about security features, data protection, and compliance requirements.'
        },
        {
          id: 6,
          icon: 'question-circle',
          title: 'General Inquiries',
          description: 'Ask any questions about the platform features, user management, or best practices.'
        }
      ],
      commonQuestions: [
        "How do I create a new invoice?",
        "Why is my invoice stuck in pending status?",
        "How do I reset my password?",
        "What are the system requirements?",
        "How do I export my invoice data?",
        "How do I add a new user to my team?"
      ]
    };
  },
  computed: {
    healthStatusClass() {
      if (!this.healthData) return 'is-warning';
      return this.healthData.status === 'healthy' ? 'is-success' : 'is-danger';
    },
    healthStatusIcon() {
      if (!this.healthData) return 'spinner';
      return this.healthData.status === 'healthy' ? 'check-circle' : 'times-circle';
    },
    healthStatusText() {
      if (!this.healthData) return 'Checking...';
      return this.healthData.status === 'healthy' ? 'Online' : 'Offline';
    }
  },
  async mounted() {
    await this.checkHealth();
  },
  methods: {
    async checkHealth() {
      this.checking = true;
      try {
        this.healthData = await chatbotService.checkHealth();
      } catch (error) {
        console.error('Health check failed:', error);
        this.healthData = { 
          status: 'unhealthy', 
          test_query_success: false,
          error: error.message 
        };
      } finally {
        this.checking = false;
      }
    },
    openChatbot() {
      // Trigger the chatbot to open - this would be handled by emitting an event
      // or by directly manipulating the chatbot component
      const chatButton = document.querySelector('.chat-button');
      if (chatButton) {
        chatButton.click();
      }
    },
    askQuestion(question) {
      // Open chatbot and send the question
      this.openChatbot();
      // You could also emit an event here to pre-fill the question
      this.$emit('ask-question', question);
    },
    scrollToFeatures() {
      this.$refs.featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
};
</script>

<style scoped>
.chatbot-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  margin: 0 auto;
}

.chatbot-icon {
  font-size: 80px;
  color: white;
}

.feature-card {
  height: 100%;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #0a1821;
  color: white;
  border-radius: 8px;
  font-size: 24px;
}

.question-item {
  margin-bottom: 8px;
}

.question-item .button {
  text-align: left;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.question-item .button:hover {
  background: #f8f9fa;
  border-color: #0a1821;
  color: #0a1821;
}

.hero {
  background: linear-gradient(135deg, #0a1821 0%, #0a3954 100%);
}

.has-background-primary {
  background: linear-gradient(135deg, #0a1821 0%, #0a3954 100%) !important;
}

.level-item .tag.is-large {
  font-size: 1rem;
  padding: 0.75rem 1rem;
}
</style>
